import {
  FlawedItemType,
  NormalizedFlawedEventType,
  normalizeFlawedEvent,
  RawFlawedEventType,
  FLAWED_UX_EVENT_NAME,
} from '@vp/flawed-ux-tracking';
import { sortBy } from 'lodash';
import { MOD_ROUTE, pageViewed, track } from '../apis/trackingApi';
import { pricingSelectors } from '../state/pricing';
import { cartSelectors } from '../state/cart';
import { EXPERIENCES, REASONS } from '../constants/flawedUxTracking';
import {
  CART_PAGE_NAME,
  CART_PAGE_SECTION,
  CART_PAGE_STAGE,
} from '../constants/cartui';
import { cartItemSelectors } from '../state/cartItems';
import type {
  CartItem,
  CartItemsState,
  CartState,
  PricingState,
} from '../types';
import {
  CART_STEP_VIEWED_HEADER,
  CHECKOUT_STEP_VIEWED_EVENT_NAME,
} from '../constants/tracking';
import trackingSelectors from './trackingSelectors';

/*
  This only reason this file (segmentTracker.ts) is separated out from the "trackingApi.js" is so that we can migrate to typescript
  gradually. As we continue to update/add new tracking functions they should be moved to this file so that they can utilize
  typescript as well.
 */

const trackFlawedUx = (data: RawFlawedEventType) => {
  const normalizedData: NormalizedFlawedEventType = normalizeFlawedEvent(data);
  track(FLAWED_UX_EVENT_NAME, normalizedData);
};

/* global QuantumMetricAPI */
declare global {
  const QuantumMetricAPI: any;
}
const getQmReplayLink = () => {
  try {
    // If QM's API is available, use that because it's authoritative
    if (
      typeof QuantumMetricAPI !== 'undefined' &&
      !!QuantumMetricAPI.getReplay
    ) {
      return QuantumMetricAPI.getReplay();
    }

    // ... but QM may not have loaded yet
    // If that's the case, see if a prior page has left a cookie behind for us
    if (typeof document !== 'undefined' && !!document.cookie) {
      const cookieMatches = /QuantumMetricSessionID=([^;]+)/.exec(
        document.cookie
      );
      if (cookieMatches) {
        const sessionId = cookieMatches[1];
        if (sessionId) {
          return `https://vistaprint.quantummetric.com/#/users/search?autoreplay=true&qmsessioncookie=${sessionId}`;
        }
      }
    }
  } catch {
    // Don't let QM rain on our parade
  }
  return '';
};

/**
 * collect the data used to send to snowflake for FLAWED_UX_TRACKING
 */
const flawedUxCommonData = (
  cart: CartState,
  cartItems: CartItemsState,
  siteName: string
): Omit<
  RawFlawedEventType,
  'experience' | 'impactedItems' | 'isApiFailure' | 'reasons'
> => ({
  source: 'CartUI',
  cartId: cartSelectors.selectCartId(cart),
  tenant: cartSelectors.selectTenant(cart),
  site: siteName,
  shopperId: cartSelectors.selectShopperId(cart),
  products:
    cartItems.cartItemIds?.map(id =>
      cartItemSelectors.selectProductKeyByItemId(cartItems, id)
    ) || [],
  qmReplay: getQmReplayLink(),
  pageName: CART_PAGE_NAME,
  pageSection: CART_PAGE_SECTION,
  pageStage: CART_PAGE_STAGE,
  additionalContext: {
    invalidValidityChecks: cartSelectors.selectInvalidValidityChecks(cart),
  },
});

/**
 * collect the FLAWED_UX_TRACKING data for individual item data
 */
const flawedUxItemData = (
  cartItems: CartItemsState,
  id: string
): Omit<FlawedItemType, 'reasons'> => ({
  itemId: id,
  productKey: cartItemSelectors.selectProductKeyByItemId(cartItems, id) || '',
  productVersion:
    cartItemSelectors.selectProductVersionByItemId(cartItems, id) || 0,
  attributes: cartItemSelectors.selectProductAttributeList(cartItems, id) || [],
  quantity: cartItemSelectors.selectQuantityByItemId(cartItems, id) || 0,
});

/**
 * This is intended for generic tracking of when the 'Checkout' button is disabled.
 *
 * Every event is tagged with the Checkout Disabled Tag and could be supplemented with:
 *  if products are invalid - Product Spec Invalid Tag
 *  if pricing is invalid - Pricing Invalid Tag.
 *
 * On an item level items will be included in the event if they are invalid for
 * checkout and tagged with any:
 *  - PODS checkout disabled reasons
 *  - pricing invalidity reason
 */
export function flawedCheckoutDisabledExperience(
  cart: CartState,
  cartItems: CartItemsState,
  pricing: any,
  siteName: string
) {
  const commonData = flawedUxCommonData(cart, cartItems, siteName);
  const topLevelReasons = [];
  if (cartSelectors.selectAreAnyProductSpecsInvalid(cart)) {
    topLevelReasons.push(REASONS.PRODUCT_SPEC_INVALID_TAG);
  }
  if (cartSelectors.selectAreAnyItemsPricingInvalid(cart)) {
    topLevelReasons.push(REASONS.PRICING_INVALID_TAG);
  }

  const itemData: FlawedItemType[] = Object.values(cartItems.cartItems)
    .filter(
      // removed items remain in `cartItems.cartItems` with a value of `undefined`
      (item?: CartItem) => item?.checkoutData?.validForCheckout === 'false'
    )
    .map((item: CartItem) => {
      const data = flawedUxItemData(cartItems, item.id);

      const itemLevelReasons = [
        ...(item.checkoutData.podsCheckoutDisabledReasons?.split(',') ?? []),
        !pricingSelectors.selectIsPricingValidForItemId(pricing, item.id) &&
          pricingSelectors.selectPricingInvalidityReasonForItemId(
            pricing,
            item.id
          ),
      ].filter(Boolean);

      return {
        ...data,
        reasons: itemLevelReasons,
      };
    });

  const flawedCheckoutDisabledExperienceData: RawFlawedEventType =
    Object.assign(commonData, {
      reasons: topLevelReasons,
      impactedItems: itemData,
      experience: EXPERIENCES.CHECKOUT_DISABLED,
      isApiFailure: false,
    });

  trackFlawedUx(flawedCheckoutDisabledExperienceData);
}

// START V1 FLAWED UX

export const V1_FLAWED_UX_NAME = 'FLAWED_UX_TRACKING';

/*
 * Code to send FLAWED_UX_TRACKING segment data
 */
const trackFlawedUxV1 = (data: any) => {
  track(V1_FLAWED_UX_NAME, data);
};

/**
 * collect the data used to send to snowflake for FLAWED_UX_TRACKING
 */
const flawedUXCommonData = (
  cart: CartState,
  pricing: any,
  siteName: string
) => ({
  route: MOD_ROUTE,
  cart_id: cartSelectors.selectCartId(cart),
  tenant: cartSelectors.selectTenant(cart),
  site: siteName,
  shopper_id: cartSelectors.selectShopperId(cart),
  currency: pricingSelectors.selectCurrency(pricing),
  items: [],
  qm_replay: getQmReplayLink(),
  invalidValidityChecks: cartSelectors.selectInvalidValidityChecks(cart),
});

// sort the list alphabetically by key
const normalizeAttributes = (attributes: { key: string }[]) =>
  sortBy(attributes, 'key');

/**
 * collect the FLAWED_UX_TRACKING data for individual item data
 */
const flawedUXItemData = (item: CartItem, cartItems: CartItemsState) => ({
  id: item.id,
  productKey: cartItemSelectors.selectProductKeyByItemId(cartItems, item.id),
  productVersion: cartItemSelectors.selectProductVersionByItemId(
    cartItems,
    item.id
  ),
  fullfillment_sku: cartItemSelectors.selectFulfillmentSkuByItemId(
    cartItems,
    item.id
  ),
  fulfillment_sku_version:
    cartItemSelectors.selectFulfillmentSkuVersionByItemId(cartItems, item.id),
  attributes: normalizeAttributes(
    cartItemSelectors.selectProductAttributeList(cartItems, item.id)
  ),
  quantity: cartItemSelectors.selectQuantityByItemId(cartItems, item.id),
  ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, item.id),
});

/**
 * This is intended for generic tracking of when the 'Checkout' button is disabled.
 *
 * Every event is tagged with the Checkout Disabled Tag and could be supplemented with:
 *  if products are invalid - Product Spec Invalid Tag
 *  if pricing is invalid - Pricing Invalid Tag.
 *
 * On an item level items will be included in the event if they are invalid for
 * checkout and tagged with any:
 *  - PODS checkout disabled reasons
 *  - pricing invalidity reason
 */
export function v1FlawedCheckoutDisabledExperience(
  cart: CartState,
  cartItems: CartItemsState,
  pricing: any,
  siteName: string
) {
  const commonData = flawedUXCommonData(cart, pricing, siteName);
  const reasonTags = [REASONS.CHECKOUT_DISABLED_TAG];
  if (cartSelectors.selectAreAnyProductSpecsInvalid(cart)) {
    reasonTags.push(REASONS.PRODUCT_SPEC_INVALID_TAG);
  }
  if (cartSelectors.selectAreAnyItemsPricingInvalid(cart)) {
    reasonTags.push(REASONS.PRICING_INVALID_TAG);
  }
  const itemData: any[] = [];
  cartItems.cartItemIds?.forEach(id => {
    const item = cartItems.cartItems[id];
    if (item.checkoutData.validForCheckout === 'false') {
      const data = flawedUXItemData(item, cartItems);
      const itemTags =
        item.checkoutData.podsCheckoutDisabledReasons?.split(',') || [];
      if (!pricingSelectors.selectIsPricingValidForItemId(pricing, id)) {
        itemTags.push(
          pricingSelectors.selectPricingInvalidityReasonForItemId(pricing, id)
        );
      }
      data.itemTags = itemTags;
      itemData.push(data);
    }
  });
  const flawedCheckoutDisabledExperienceData = Object.assign(commonData, {
    tags: reasonTags,
    items: itemData,
  });

  trackFlawedUxV1(flawedCheckoutDisabledExperienceData);
}

// END V1 FLAWED UX

/**
 * Tracking event for Cart page  (Checkout Step 1) viewed.
 * Note: Viewing the Cart page is considered the first step in checkout for analytics.
 * @see "Product Funnel Tracking specification for Google Analytics" @ https://vistaprint.atlassian.net
 */
export const trackCartStepViewed = (
  cart: CartState,
  cartItems: CartItemsState,
  pricing: PricingState
) => {
  track(
    CHECKOUT_STEP_VIEWED_EVENT_NAME,
    trackingSelectors.selectCheckoutStepData({ cart, cartItems, pricing })
  );
};

/**
 * Page View event for Cart page.
 * Note: Segment does not map any product-related information currently through to GA with this call.
 * @see "Product Funnel Tracking specification for Google Analytics" @ https://vistaprint.atlassian.net
 */
export const trackCartPageViewed = () => {
  const { pageName, pageStage, pageSection } = CART_STEP_VIEWED_HEADER;
  pageViewed(CART_PAGE_NAME, { pageName, pageSection, pageStage });
};
